import {useQuery} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Switch} from '@/components/ui/switch'
import {Accordion, AccordionContent, AccordionItem, AccordionTrigger} from '@/components/ui/accordion'
import {AttributeValueType} from '@/types/attribute'
import type {ValidationRules, ListEntry} from '@/types/attribute-definition'
import type {AttributeEntityType} from '@/types/attribute-entity-type'
import {ValueSourceType} from '@/types/attribute-definition'
import {useState, useMemo} from 'react'
import {Code, Sparkles, Plus, Trash2} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {ValueSourcePreview} from './ValueSourcePreview'
import {SqlEditorDialog} from './SqlEditorDialog'
import {TypeScopeSelector} from './TypeScopeSelector'
import type {ValueList} from '@/types/value-list'

const VALUE_TYPES = AttributeValueType.all()

 
const VALUE_SOURCE_NONE = 'NONE'
const RULE_PATTERN = 'pattern'
const RULE_MIN_LENGTH = 'minLength'
const RULE_MAX_LENGTH = 'maxLength'
const RULE_MIN = 'min'
const RULE_MAX = 'max'
const FIELD_PATH_PATTERN = 'validationRules/pattern'
const FIELD_PATH_MIN_LENGTH = 'validationRules/minLength'
const FIELD_PATH_MAX_LENGTH = 'validationRules/maxLength'
const FIELD_PATH_MIN = 'validationRules/min'
const FIELD_PATH_MAX = 'validationRules/max'
const ERROR_BORDER = 'border-destructive'
 

export interface DefinitionFieldValues {
    name: string
    displayName: string
    displayNameTranslations: Record<string, string>
    valueType: string
    required: boolean
    active: boolean
    sortOrder: number
    validationRules: ValidationRules | null
    typeCodes: string[]
    searchable: boolean
    filterable: boolean
}

interface DefinitionFieldsProps {
    values: DefinitionFieldValues
    onChange: (updates: Partial<DefinitionFieldValues>) => void
    layout: 'horizontal' | 'vertical'
    nameDisabled?: boolean
    allDisabled?: boolean
    nameError?: string | null
    fieldErrors?: Record<string, string>
    testIdPrefix?: string
    configuredLocales?: string[]
    onSuggestPattern?: () => void
    entityType?: AttributeEntityType
}

export function DefinitionFields({values, onChange, layout, nameDisabled, allDisabled, nameError, fieldErrors, testIdPrefix, configuredLocales, onSuggestPattern, entityType}: DefinitionFieldsProps) {
    const i18n = useI18n()
    const services = useServices()
    const disabled = !!allDisabled

    const sourceType = values.validationRules?.valueSource?.type ?? VALUE_SOURCE_NONE
    const enumTypeName = values.validationRules?.valueSource?.typeName ?? ''
    const maxOccurrences = values.validationRules?.maxOccurrences


    const {data: valueSources} = useQuery<string[]>({
        queryKey: ['allowed-value-sources'],
        queryFn: () => services.attributeDefinitions.allowedValueSources(),
    })

    const {data: customValueListsPage} = useQuery({
        queryKey: ['value-lists', 'all'],
        queryFn: () => services.valueLists.list({size: 1000}),
    })
    const customValueLists: ValueList[] = customValueListsPage?.content ?? []

    const customListName = values.validationRules?.valueSource?.listName ?? ''

    function setValueSource(newSourceType: string, typeName?: string, listName?: string) {
        const base = values.validationRules ?? {}
        const newIsListBased = newSourceType === ValueSourceType.LIST
            || newSourceType === ValueSourceType.ENUM
            || newSourceType === ValueSourceType.CUSTOM_LIST
        const cleared = newIsListBased
            ? {pattern: undefined, minLength: undefined, maxLength: undefined, min: undefined, max: undefined}
            : {}
        let validationRules: ValidationRules | null
        if (newSourceType === ValueSourceType.ENUM) {
            validationRules = {...base, ...cleared, valueSource: {type: ValueSourceType.ENUM, typeName: typeName ?? (enumTypeName || undefined)}}
        } else if (newSourceType === ValueSourceType.LIST) {
            validationRules = {...base, ...cleared, valueSource: {type: ValueSourceType.LIST, values: []}}
        } else if (newSourceType === ValueSourceType.SQL) {
            validationRules = {...base, valueSource: {type: ValueSourceType.SQL}}
        } else if (newSourceType === ValueSourceType.SQL_LABELED) {
            validationRules = {...base, valueSource: {type: ValueSourceType.SQL_LABELED}}
        } else if (newSourceType === ValueSourceType.CUSTOM_LIST) {
            validationRules = {...base, ...cleared, valueSource: {type: ValueSourceType.CUSTOM_LIST, listName: listName ?? (customListName || undefined)}}
        } else {
            validationRules = {...base, valueSource: undefined}
        }
        onChange({validationRules})
    }

    function setMaxOccurrences(value: string) {
        const base = values.validationRules ?? {}
        const num = value !== '' ? parseInt(value, 10) : undefined
        onChange({validationRules: {...base, maxOccurrences: num !== undefined && !isNaN(num) ? num : undefined}})
    }

    const listEntries: ListEntry[] = values.validationRules?.valueSource?.type === ValueSourceType.LIST
        ? (values.validationRules.valueSource.values ?? [])
        : []

    function setListEntries(entries: ListEntry[]) {
        const base = values.validationRules ?? {}
        onChange({validationRules: {...base, valueSource: {type: ValueSourceType.LIST, values: entries}}})
    }

    function addListEntry() {
        setListEntries([...listEntries, {code: '', label: ''}])
    }

    function updateListEntryCode(index: number, value: string) {
        const updated = listEntries.map((e, i) => i === index ? {...e, code: value} : e)
        setListEntries(updated)
    }

    function updateListEntryLabel(index: number, value: string) {
        const updated = listEntries.map((e, i) => i === index ? {...e, label: value} : e)
        setListEntries(updated)
    }

    function removeListEntry(index: number) {
        setListEntries(listEntries.filter((_, i) => i !== index))
    }

    function listEntryCodeError(index: number): string | undefined {
        const entry = listEntries[index]
        if (!entry) return undefined
        if (!entry.code || entry.code.trim() === '') return i18n.attributeDefinitions.validationRules.listEditor.errorBlankCode
        const isDuplicate = listEntries.some((e, i) => i !== index && e.code === entry.code)
        if (isDuplicate) return i18n.attributeDefinitions.validationRules.listEditor.errorDuplicateCode
        return undefined
    }

    function listEntryLabelError(index: number): string | undefined {
        const entry = listEntries[index]
        if (!entry) return undefined
        if (!entry.label || entry.label.trim() === '') return i18n.attributeDefinitions.validationRules.listEditor.errorBlankLabel
        return undefined
    }

    const pattern = values.validationRules?.pattern ?? ''
    const minLength = values.validationRules?.minLength
    const maxLength = values.validationRules?.maxLength
    const min = values.validationRules?.min
    const max = values.validationRules?.max

    const isStringType = values.valueType === AttributeValueType.STRING
    const isNumberType = values.valueType === AttributeValueType.NUMBER

    const isListBasedSource = sourceType === ValueSourceType.LIST
        || sourceType === ValueSourceType.ENUM
        || sourceType === ValueSourceType.CUSTOM_LIST

    const showPattern = !isListBasedSource && (isStringType || !!pattern)
    const showMinLength = !isListBasedSource && (isStringType || minLength != null)
    const showMaxLength = !isListBasedSource && (isStringType || maxLength != null)
    const showMin = !isListBasedSource && (isNumberType || min != null)
    const showMax = !isListBasedSource && (isNumberType || max != null)

    function fieldError(fieldPath: string): string | undefined {
        return fieldErrors?.[fieldPath]
    }

    function errorStyle(fieldPath: string): string {
        return fieldError(fieldPath) ? ERROR_BORDER : ''
    }

    function setValidationRule(field: string, value: string) {
        const base = values.validationRules ?? {}
        if (field === 'pattern') {
            onChange({validationRules: {...base, pattern: value || undefined}})
        } else {
            const num = value ? parseFloat(value) : undefined
            onChange({validationRules: {...base, [field]: num !== undefined && !isNaN(num) ? num : undefined}})
        }
    }

    const [sqlEditorOpen, setSqlEditorOpen] = useState(false)

    const nameValid = !values.name || (/^[a-zA-Z0-9_]+$/.test(values.name) && values.name.length <= 100)
    // eslint-disable-next-line i18next/no-literal-string -- test ID prefix
    const prefix = testIdPrefix ?? 'def'

    // Count non-default validation items for the badge
    const validationCount = useMemo(() => {
        let count = 0
        const rules = values.validationRules
        if (!rules) return 0
        if (rules.pattern) count++
        if (rules.minLength != null) count++
        if (rules.maxLength != null) count++
        if (rules.min != null) count++
        if (rules.max != null) count++
        if (rules.valueSource) count++
        if (rules.maxOccurrences != null) count++
        return count
    }, [values.validationRules])

    function handleSqlApply(sql: string) {
        const base = values.validationRules ?? {}
        const type = sourceType === ValueSourceType.SQL_LABELED ? ValueSourceType.SQL_LABELED : ValueSourceType.SQL
        onChange({validationRules: {...base, valueSource: {type, sql}}})
    }

    if (layout === 'horizontal') {
        return (
        <>
            <div className="flex items-center gap-2 flex-wrap">
                <Input
                    data-testid={`${prefix}-name`}
                    value={values.displayName || values.name}
                    onChange={e => onChange({displayName: e.target.value})}
                    onBlur={e => { if (!e.target.value.trim()) onChange({displayName: values.name}) }}
                    disabled={disabled}
                    className="w-36"
                />
                <Select value={values.valueType} onValueChange={valueType => onChange({valueType})} disabled={disabled}>
                    <SelectTrigger data-testid={`${prefix}-type`} className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {VALUE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={sourceType} onValueChange={v => setValueSource(v)} disabled={disabled}>
                    <SelectTrigger data-testid={`${prefix}-source`} className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value={VALUE_SOURCE_NONE}>—</SelectItem>
                        <SelectItem value={ValueSourceType.LIST}>{i18n.attributeDefinitions.valueSourceTypes.LIST}</SelectItem>
                        <SelectItem value={ValueSourceType.ENUM}>{i18n.attributeDefinitions.valueSourceTypes.ENUM}</SelectItem>
                        <SelectItem value={ValueSourceType.SQL}>{i18n.attributeDefinitions.valueSourceTypes.SQL}</SelectItem>
                        <SelectItem value={ValueSourceType.SQL_LABELED}>{i18n.attributeDefinitions.valueSourceTypes.SQL_LABELED}</SelectItem>
                        <SelectItem value={ValueSourceType.CUSTOM_LIST}>{i18n.attributeDefinitions.valueSourceTypes.CUSTOM_LIST}</SelectItem>
                    </SelectContent>
                </Select>
                {sourceType === ValueSourceType.ENUM && (
                    <Select value={enumTypeName} onValueChange={typeName => setValueSource(sourceType, typeName)} disabled={disabled}>
                        <SelectTrigger data-testid={`${prefix}-enum`} className="w-40"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {(valueSources ?? []).map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}
                {sourceType === ValueSourceType.CUSTOM_LIST && (
                    <Select value={customListName} onValueChange={ln => setValueSource(sourceType, undefined, ln)} disabled={disabled}>
                        <SelectTrigger data-testid={`${prefix}-custom-list`} className="w-48"><SelectValue placeholder={i18n.valueLists.selectList} /></SelectTrigger>
                        <SelectContent>
                            {customValueLists.map(l => <SelectItem key={l.id} value={l.name}>{l.displayName}</SelectItem>)}
                        </SelectContent>
                    </Select>
                )}
                {(sourceType === ValueSourceType.SQL || sourceType === ValueSourceType.SQL_LABELED) && (
                    <Button
                        variant="outline"
                        size="sm"
                        data-testid={`${prefix}-sql`}
                        onClick={() => setSqlEditorOpen(true)}
                        disabled={disabled}
                        className="w-64 text-xs font-mono justify-start truncate"
                    >
                        <Code className="h-3 w-3 mr-1 shrink-0" />
                        {values.validationRules?.valueSource?.sql
                            ? <span className="truncate">{values.validationRules.valueSource.sql}</span>
                            : <span className="text-muted-foreground">SQL...</span>}
                    </Button>
                )}
                {sourceType !== VALUE_SOURCE_NONE && values.validationRules?.valueSource && (
                    <ValueSourcePreview definition={{validationRules: values.validationRules} as never} testId={`${prefix}-preview`} />
                )}
                {showPattern && (
                    <div className="flex items-center gap-2">
                        <Input
                            data-testid={`${prefix}-pattern`}
                            value={pattern}
                            onChange={e => setValidationRule(RULE_PATTERN, e.target.value)}
                            placeholder={i18n.attributeDefinitions.validationRules.patternPlaceholder}
                            disabled={disabled}
                            className={`w-32 text-xs font-mono ${errorStyle(FIELD_PATH_PATTERN)}`}
                        />
                        {onSuggestPattern && (
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={onSuggestPattern}
                                disabled={disabled}
                                title={i18n.attributeDefinitions.patternSuggest.title}
                                data-testid={`${prefix}-pattern-suggest`}
                            >
                                <Sparkles className="h-3 w-3" />
                            </Button>
                        )}
                        {fieldError(FIELD_PATH_PATTERN) && <p data-testid={`${prefix}-validationRules/pattern-error`} className="text-xs text-destructive mt-1">{fieldError(FIELD_PATH_PATTERN)}</p>}
                    </div>
                )}
                {showMinLength && (
                    <div>
                        <Input
                            data-testid={`${prefix}-minlength`}
                            type="number" min={1}
                            value={minLength ?? ''}
                            onChange={e => setValidationRule(RULE_MIN_LENGTH, e.target.value)}
                            placeholder={i18n.attributeDefinitions.validationRules.minLengthPlaceholder}
                            disabled={disabled}
                            className={`w-16 text-xs ${errorStyle(FIELD_PATH_MIN_LENGTH)}`}
                        />
                        {minLength != null && minLength < 1 && <p data-testid={`${prefix}-minlength-inline-error`} className="text-xs text-destructive mt-1">{i18n.attributeDefinitions.validationRules.mustBeAtLeastOne}</p>}
                        {fieldError(FIELD_PATH_MIN_LENGTH) && <p data-testid={`${prefix}-validationRules/minLength-error`} className="text-xs text-destructive mt-1">{fieldError(FIELD_PATH_MIN_LENGTH)}</p>}
                    </div>
                )}
                {showMaxLength && (
                    <div>
                        <Input
                            data-testid={`${prefix}-maxlength`}
                            type="number" min={1}
                            value={maxLength ?? ''}
                            onChange={e => setValidationRule(RULE_MAX_LENGTH, e.target.value)}
                            placeholder={i18n.attributeDefinitions.validationRules.maxLengthPlaceholder}
                            disabled={disabled}
                            className={`w-16 text-xs ${errorStyle(FIELD_PATH_MAX_LENGTH)}`}
                        />
                        {maxLength != null && maxLength < 1 && <p data-testid={`${prefix}-maxlength-inline-error`} className="text-xs text-destructive mt-1">{i18n.attributeDefinitions.validationRules.mustBeAtLeastOne}</p>}
                        {fieldError(FIELD_PATH_MAX_LENGTH) && <p data-testid={`${prefix}-validationRules/maxLength-error`} className="text-xs text-destructive mt-1">{fieldError(FIELD_PATH_MAX_LENGTH)}</p>}
                    </div>
                )}
                {showMin && (
                    <div>
                        <Input
                            data-testid={`${prefix}-min`}
                            type="number"
                            value={min ?? ''}
                            onChange={e => setValidationRule(RULE_MIN, e.target.value)}
                            placeholder={i18n.attributeDefinitions.validationRules.minPlaceholder}
                            disabled={disabled}
                            className={`w-16 text-xs ${errorStyle(FIELD_PATH_MIN)}`}
                        />
                        {fieldError(FIELD_PATH_MIN) && <p data-testid={`${prefix}-validationRules/min-error`} className="text-xs text-destructive mt-1">{fieldError(FIELD_PATH_MIN)}</p>}
                    </div>
                )}
                {showMax && (
                    <div>
                        <Input
                            data-testid={`${prefix}-max`}
                            type="number"
                            value={max ?? ''}
                            onChange={e => setValidationRule(RULE_MAX, e.target.value)}
                            placeholder={i18n.attributeDefinitions.validationRules.maxPlaceholder}
                            disabled={disabled}
                            className={`w-16 text-xs ${errorStyle(FIELD_PATH_MAX)}`}
                        />
                        {fieldError(FIELD_PATH_MAX) && <p data-testid={`${prefix}-validationRules/max-error`} className="text-xs text-destructive mt-1">{fieldError(FIELD_PATH_MAX)}</p>}
                    </div>
                )}
                <div>
                    <Input
                        data-testid={`${prefix}-maxocc`}
                        type="number" min={1}
                        value={maxOccurrences ?? ''}
                        onChange={e => setMaxOccurrences(e.target.value)}
                        placeholder={i18n.common.maxValues ?? 'Max values'} // eslint-disable-line i18next/no-literal-string
                        disabled={disabled}
                        className="w-20 text-xs"
                    />
                    {maxOccurrences != null && maxOccurrences < 1 && <p data-testid={`${prefix}-maxocc-inline-error`} className="text-xs text-destructive mt-1">{i18n.attributeDefinitions.validationRules.mustBeAtLeastOne}</p>}
                </div>
                <div className="flex items-center gap-1">
                    <Switch data-testid={`${prefix}-required`} checked={values.required} onCheckedChange={required => onChange({required})} disabled={disabled} />
                    <Label className="text-xs text-muted-foreground">{i18n.common.required}</Label>
                </div>
                {values.valueType === AttributeValueType.STRING && !values.validationRules?.valueSource && !(values.validationRules?.maxOccurrences && values.validationRules.maxOccurrences > 1) && (
                <div className="flex items-center gap-1">
                    <Switch data-testid={`${prefix}-unique`} checked={values.validationRules?.unique ?? false} onCheckedChange={unique => onChange({validationRules: {...(values.validationRules ?? {}), unique: unique || undefined}})} disabled={disabled} />
                    <Label className="text-xs text-muted-foreground">{i18n.attributeDefinitions.fields.unique}</Label>
                </div>
                )}
            </div>

            {sourceType === ValueSourceType.LIST && (
                <div className="mt-2 grid gap-1" data-testid={`${prefix}-list-editor`}>
                    {listEntries.length > 0 && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground font-medium mb-1">
                            <span className="w-28">{i18n.attributeDefinitions.validationRules.listEditor.codeHeader}</span>
                            <span className="w-40">{i18n.attributeDefinitions.validationRules.listEditor.labelHeader}</span>
                        </div>
                    )}
                    {listEntries.map((entry, index) => (
                        <div key={index} className="flex items-start gap-2">
                            <div>
                                <Input
                                    data-testid={`${prefix}-list-entry-code-${index}`}
                                    value={entry.code}
                                    onChange={e => updateListEntryCode(index, e.target.value)}
                                    placeholder={i18n.attributeDefinitions.validationRules.listEditor.codePlaceholder}
                                    disabled={disabled}
                                    className={`w-28 text-xs font-mono ${listEntryCodeError(index) ? ERROR_BORDER : ''}`}
                                />
                                {listEntryCodeError(index) && <p className="text-xs text-destructive mt-0.5">{listEntryCodeError(index)}</p>}
                            </div>
                            <div>
                                <Input
                                    data-testid={`${prefix}-list-entry-label-${index}`}
                                    value={entry.label}
                                    onChange={e => updateListEntryLabel(index, e.target.value)}
                                    placeholder={i18n.attributeDefinitions.validationRules.listEditor.labelPlaceholder}
                                    disabled={disabled}
                                    className={`w-40 text-xs ${listEntryLabelError(index) ? ERROR_BORDER : ''}`}
                                />
                                {listEntryLabelError(index) && <p className="text-xs text-destructive mt-0.5">{listEntryLabelError(index)}</p>}
                            </div>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                data-testid={`${prefix}-list-entry-remove-${index}`}
                                onClick={() => removeListEntry(index)}
                                disabled={disabled}
                                className="h-8 w-8 p-0"
                            >
                                <Trash2 className="h-3 w-3" />
                            </Button>
                        </div>
                    ))}
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid={`${prefix}-list-entries-add`}
                        onClick={addListEntry}
                        disabled={disabled}
                        className="mt-1 w-auto self-start"
                    >
                        <Plus className="h-3 w-3 mr-1" />
                        {i18n.attributeDefinitions.validationRules.listEditor.addEntry}
                    </Button>
                </div>
            )}

            <SqlEditorDialog
                open={sqlEditorOpen}
                onOpenChange={setSqlEditorOpen}
                sql={values.validationRules?.valueSource?.sql ?? ''}
                onApply={handleSqlApply}
                parametersHint={sourceType === ValueSourceType.SQL_LABELED ? i18n.attributeDefinitions.sqlHintTwoColumns : i18n.attributeDefinitions.sqlHintOneColumn}
                validateResult={result => {
                    const expected = sourceType === ValueSourceType.SQL_LABELED ? 2 : 1
                    if (result.columns.length !== expected) {
                        return expected === 2
                            ? i18n.attributeDefinitions.sqlLabeledColumnCount({count: result.columns.length, columns: result.columns.map(c => c.name).join(', ')})
                            : i18n.attributeDefinitions.sqlTooManyColumns({count: result.columns.length, columns: result.columns.map(c => c.name).join(', ')})
                    }
                    return null
                }}
                maxRows={5}
            />
        </>
        )
    }

    // vertical layout (dialog form) with accordion sections
    return (
        <>
        {entityType && (
            <div className="grid gap-2 mb-4">
                <Label>{i18n.attributeDefinitions.fields.typeScope}</Label>
                <TypeScopeSelector
                    entityType={entityType}
                    value={values.typeCodes}
                    onChange={typeCodes => onChange({typeCodes})}
                    disabled={disabled}
                />
            </div>
        )}
        <Accordion type="multiple" defaultValue={['basic', 'behavior']} className="w-full">
            {/* ── Basic ── */}
            <AccordionItem value="basic">
                <AccordionTrigger>{i18n.attributeDefinitions.sections.basic}</AccordionTrigger>
                <AccordionContent>
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <Label htmlFor={`${prefix}-name`}>{i18n.attributeDefinitions.fields.name}</Label>
                            <Input
                                id={`${prefix}-name`}
                                data-testid={`${prefix}-name`}
                                value={values.name}
                                onChange={e => onChange({name: e.target.value})}
                                placeholder={i18n.attributeDefinitions.fields.namePlaceholder}
                                maxLength={100}
                                disabled={nameDisabled || disabled}
                                className={nameDisabled ? 'font-mono' : ''}
                            />
                            {nameError && <p className="text-sm text-destructive">{nameError}</p>}
                            {values.name && !nameValid && <p className="text-sm text-destructive">{i18n.attributeDefinitions.nameInvalid}</p>}
                        </div>

                        {configuredLocales && configuredLocales.length > 0 ? (
                            configuredLocales.map(locale => (
                                <div key={locale} className="grid gap-2">
                                    <Label htmlFor={`${prefix}-display-name-${locale}`}>
                                        {i18n.attributeDefinitions.fields.displayNameLocale({locale: locale.toUpperCase()})}
                                    </Label>
                                    <Input
                                        id={`${prefix}-display-name-${locale}`}
                                        data-testid={`${prefix}-display-name-${locale}`}
                                        value={values.displayNameTranslations[locale] ?? ''}
                                        onChange={e => onChange({displayNameTranslations: {...values.displayNameTranslations, [locale]: e.target.value}})}
                                        placeholder={i18n.attributeDefinitions.fields.displayNamePlaceholder}
                                        maxLength={255}
                                        disabled={disabled}
                                    />
                                </div>
                            ))
                        ) : (
                            <div className="grid gap-2">
                                <Label htmlFor={`${prefix}-display-name`}>{i18n.attributeDefinitions.fields.displayName}</Label>
                                <Input
                                    id={`${prefix}-display-name`}
                                    data-testid={`${prefix}-display-name`}
                                    value={values.displayName}
                                    onChange={e => onChange({displayName: e.target.value})}
                                    placeholder={i18n.attributeDefinitions.fields.displayNamePlaceholder}
                                    maxLength={255}
                                    disabled={disabled}
                                />
                            </div>
                        )}

                        <div className="grid gap-2">
                            <Label>{i18n.attributeDefinitions.fields.valueType}</Label>
                            <Select value={values.valueType} onValueChange={valueType => onChange({valueType})} disabled={disabled}>
                                <SelectTrigger data-testid={`${prefix}-type`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {VALUE_TYPES.map(vt => <SelectItem key={vt} value={vt}>{vt}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </AccordionContent>
            </AccordionItem>

            {/* ── Behavior ── */}
            <AccordionItem value="behavior">
                <AccordionTrigger>{i18n.attributeDefinitions.sections.behavior}</AccordionTrigger>
                <AccordionContent>
                    <div className="grid gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <Switch id={`${prefix}-required`} data-testid={`${prefix}-required`} checked={values.required} onCheckedChange={required => onChange({required})} disabled={disabled} />
                                <Label htmlFor={`${prefix}-required`}>{i18n.attributeDefinitions.fields.required}</Label>
                            </div>
                            {values.valueType === AttributeValueType.STRING && !values.validationRules?.valueSource && !(values.validationRules?.maxOccurrences && values.validationRules.maxOccurrences > 1) && (
                            <div className="flex items-center gap-2">
                                <Switch id={`${prefix}-unique`} data-testid={`${prefix}-unique`} checked={values.validationRules?.unique ?? false} onCheckedChange={unique => onChange({validationRules: {...(values.validationRules ?? {}), unique: unique || undefined}})} disabled={disabled} />
                                <Label htmlFor={`${prefix}-unique`}>{i18n.attributeDefinitions.fields.unique}</Label>
                            </div>
                            )}
                            <div className="flex items-center gap-2">
                                <Switch id={`${prefix}-active`} data-testid={`${prefix}-active`} checked={values.active} onCheckedChange={active => onChange({active})} disabled={disabled} />
                                <Label htmlFor={`${prefix}-active`}>{i18n.attributeDefinitions.fields.active}</Label>
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor={`${prefix}-sort-order`}>{i18n.attributeDefinitions.fields.sortOrder}</Label>
                            <Input
                                id={`${prefix}-sort-order`}
                                data-testid={`${prefix}-sort-order`}
                                type="number"
                                value={values.sortOrder}
                                onChange={e => onChange({sortOrder: Number(e.target.value)})}
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </AccordionContent>
            </AccordionItem>

            {/* ── Validation ── */}
            <AccordionItem value="validation">
                <AccordionTrigger>
                    {validationCount > 0
                        ? i18n.attributeDefinitions.validationRules.titleWithCount({count: validationCount})
                        : i18n.attributeDefinitions.validationRules.title}
                </AccordionTrigger>
                <AccordionContent>
                    <div className="grid gap-4">
                        <div className="grid gap-2">
                            <Label>{i18n.attributeDefinitions.columns.valueSource}</Label>
                            <Select value={sourceType} onValueChange={v => setValueSource(v)} disabled={disabled}>
                                <SelectTrigger data-testid={`${prefix}-source`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value={VALUE_SOURCE_NONE}>—</SelectItem>
                                    <SelectItem value={ValueSourceType.LIST}>{i18n.attributeDefinitions.valueSourceTypes.LIST}</SelectItem>
                                    <SelectItem value={ValueSourceType.ENUM}>{i18n.attributeDefinitions.valueSourceTypes.ENUM}</SelectItem>
                                    <SelectItem value={ValueSourceType.SQL}>{i18n.attributeDefinitions.valueSourceTypes.SQL}</SelectItem>
                                    <SelectItem value={ValueSourceType.SQL_LABELED}>{i18n.attributeDefinitions.valueSourceTypes.SQL_LABELED}</SelectItem>
                                    <SelectItem value={ValueSourceType.CUSTOM_LIST}>{i18n.attributeDefinitions.valueSourceTypes.CUSTOM_LIST}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {sourceType === ValueSourceType.ENUM && (
                            <div className="grid gap-2">
                                <Label>{i18n.attributeDefinitions.fields.valueType}</Label>
                                <Select value={enumTypeName} onValueChange={typeName => setValueSource(sourceType, typeName)} disabled={disabled}>
                                    <SelectTrigger data-testid={`${prefix}-enum`}><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {(valueSources ?? []).map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {(sourceType === ValueSourceType.SQL || sourceType === ValueSourceType.SQL_LABELED) && (
                            <div className="grid gap-2">
                                <Label>SQL</Label>{/* eslint-disable-line i18next/no-literal-string -- technical label */}
                                <Button
                                    variant="outline"
                                    data-testid={`${prefix}-sql`}
                                    onClick={() => setSqlEditorOpen(true)}
                                    disabled={disabled}
                                    className="font-mono text-sm justify-start h-auto py-2 truncate"
                                >
                                    <Code className="h-4 w-4 mr-2 shrink-0" />
                                    {values.validationRules?.valueSource?.sql
                                        ? <span className="truncate text-left">{values.validationRules.valueSource.sql}</span>
                                        : <span className="text-muted-foreground">SELECT DISTINCT column FROM table</span>}{/* eslint-disable-line i18next/no-literal-string */}
                                </Button>
                            </div>
                        )}

                        {sourceType === ValueSourceType.CUSTOM_LIST && (
                            <div className="grid gap-2">
                                <Label>{i18n.valueLists.customListLabel}</Label>
                                <Select value={customListName} onValueChange={ln => setValueSource(sourceType, undefined, ln)} disabled={disabled}>
                                    <SelectTrigger data-testid={`${prefix}-custom-list`}><SelectValue placeholder={i18n.valueLists.selectList} /></SelectTrigger>
                                    <SelectContent>
                                        {customValueLists.map(l => <SelectItem key={l.id} value={l.name}>{l.displayName}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {sourceType === ValueSourceType.LIST && (
                            <div className="grid gap-2" data-testid={`${prefix}-list-editor`}>
                                <Label>{i18n.attributeDefinitions.validationRules.listEditor.title}</Label>
                                {listEntries.length > 0 && (
                                    <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs text-muted-foreground font-medium px-1">
                                        <span>{i18n.attributeDefinitions.validationRules.listEditor.codeHeader}</span>
                                        <span>{i18n.attributeDefinitions.validationRules.listEditor.labelHeader}</span>
                                        <span />
                                    </div>
                                )}
                                <div className="grid gap-2">
                                    {listEntries.map((entry, index) => (
                                        <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
                                            <div>
                                                <Input
                                                    data-testid={`${prefix}-list-entry-code-${index}`}
                                                    value={entry.code}
                                                    onChange={e => updateListEntryCode(index, e.target.value)}
                                                    placeholder={i18n.attributeDefinitions.validationRules.listEditor.codePlaceholder}
                                                    disabled={disabled}
                                                    className={`font-mono text-sm ${listEntryCodeError(index) ? ERROR_BORDER : ''}`}
                                                />
                                                {listEntryCodeError(index) && <p className="text-xs text-destructive mt-1">{listEntryCodeError(index)}</p>}
                                            </div>
                                            <div>
                                                <Input
                                                    data-testid={`${prefix}-list-entry-label-${index}`}
                                                    value={entry.label}
                                                    onChange={e => updateListEntryLabel(index, e.target.value)}
                                                    placeholder={i18n.attributeDefinitions.validationRules.listEditor.labelPlaceholder}
                                                    disabled={disabled}
                                                    className={`text-sm ${listEntryLabelError(index) ? ERROR_BORDER : ''}`}
                                                />
                                                {listEntryLabelError(index) && <p className="text-xs text-destructive mt-1">{listEntryLabelError(index)}</p>}
                                            </div>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                data-testid={`${prefix}-list-entry-remove-${index}`}
                                                onClick={() => removeListEntry(index)}
                                                disabled={disabled}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    data-testid={`${prefix}-list-entries-add`}
                                    onClick={addListEntry}
                                    disabled={disabled}
                                    className="w-full"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    {i18n.attributeDefinitions.validationRules.listEditor.addEntry}
                                </Button>
                            </div>
                        )}

                        {sourceType !== VALUE_SOURCE_NONE && values.validationRules?.valueSource && sourceType !== ValueSourceType.LIST && (
                            <ValueSourcePreview definition={{validationRules: values.validationRules} as never} testId={`${prefix}-preview`} />
                        )}

                        {showPattern && (
                            <div className="flex items-center gap-2">
                                <div className="flex-1 grid gap-2">
                                    <Label htmlFor={`${prefix}-pattern`}>{i18n.attributeDefinitions.validationRules.pattern}</Label>
                                    <Input
                                        id={`${prefix}-pattern`}
                                        data-testid={`${prefix}-pattern`}
                                        value={pattern}
                                        onChange={e => setValidationRule(RULE_PATTERN, e.target.value)}
                                        placeholder={i18n.attributeDefinitions.validationRules.patternPlaceholder}
                                        disabled={disabled}
                                        className={`font-mono text-sm ${errorStyle(FIELD_PATH_PATTERN)}`}
                                    />
                                </div>
                                {onSuggestPattern && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        onClick={onSuggestPattern}
                                        disabled={disabled}
                                        title={i18n.attributeDefinitions.patternSuggest.title}
                                        data-testid={`${prefix}-pattern-suggest`}
                                        className="mt-6"
                                    >
                                        <Sparkles className="h-4 w-4" />
                                    </Button>
                                )}
                                {fieldError(FIELD_PATH_PATTERN) && <p data-testid={`${prefix}-validationRules/pattern-error`} className="text-sm text-destructive w-full">{fieldError(FIELD_PATH_PATTERN)}</p>}
                            </div>
                        )}

                        {(showMinLength || showMaxLength) && (
                            <div className="grid grid-cols-2 gap-4">
                                {showMinLength && (
                                <div className="grid gap-2">
                                    <Label htmlFor={`${prefix}-minlength`}>{i18n.attributeDefinitions.validationRules.minLength}</Label>
                                    <Input
                                        id={`${prefix}-minlength`}
                                        data-testid={`${prefix}-minlength`}
                                        type="number" min={1}
                                        value={minLength ?? ''}
                                        onChange={e => setValidationRule(RULE_MIN_LENGTH, e.target.value)}
                                        placeholder={i18n.attributeDefinitions.validationRules.minLengthPlaceholder}
                                        disabled={disabled}
                                        className={errorStyle(FIELD_PATH_MIN_LENGTH)}
                                    />
                                    {minLength != null && minLength < 1 && <p data-testid={`${prefix}-minlength-inline-error`} className="text-sm text-destructive">{i18n.attributeDefinitions.validationRules.mustBeAtLeastOne}</p>}
                                    {fieldError(FIELD_PATH_MIN_LENGTH) && <p data-testid={`${prefix}-validationRules/minLength-error`} className="text-sm text-destructive">{fieldError(FIELD_PATH_MIN_LENGTH)}</p>}
                                </div>
                                )}
                                {showMaxLength && (
                                <div className="grid gap-2">
                                    <Label htmlFor={`${prefix}-maxlength`}>{i18n.attributeDefinitions.validationRules.maxLength}</Label>
                                    <Input
                                        id={`${prefix}-maxlength`}
                                        data-testid={`${prefix}-maxlength`}
                                        type="number" min={1}
                                        value={maxLength ?? ''}
                                        onChange={e => setValidationRule(RULE_MAX_LENGTH, e.target.value)}
                                        placeholder={i18n.attributeDefinitions.validationRules.maxLengthPlaceholder}
                                        disabled={disabled}
                                        className={errorStyle(FIELD_PATH_MAX_LENGTH)}
                                    />
                                    {maxLength != null && maxLength < 1 && <p data-testid={`${prefix}-maxlength-inline-error`} className="text-sm text-destructive">{i18n.attributeDefinitions.validationRules.mustBeAtLeastOne}</p>}
                                    {fieldError(FIELD_PATH_MAX_LENGTH) && <p data-testid={`${prefix}-validationRules/maxLength-error`} className="text-sm text-destructive">{fieldError(FIELD_PATH_MAX_LENGTH)}</p>}
                                </div>
                                )}
                            </div>
                        )}

                        {(showMin || showMax) && (
                            <div className="grid grid-cols-2 gap-4">
                                {showMin && (
                                <div className="grid gap-2">
                                    <Label htmlFor={`${prefix}-min`}>{i18n.attributeDefinitions.validationRules.min}</Label>
                                    <Input
                                        id={`${prefix}-min`}
                                        data-testid={`${prefix}-min`}
                                        type="number"
                                        value={min ?? ''}
                                        onChange={e => setValidationRule(RULE_MIN, e.target.value)}
                                        placeholder={i18n.attributeDefinitions.validationRules.minPlaceholder}
                                        disabled={disabled}
                                        className={errorStyle(FIELD_PATH_MIN)}
                                    />
                                    {fieldError(FIELD_PATH_MIN) && <p data-testid={`${prefix}-validationRules/min-error`} className="text-sm text-destructive">{fieldError(FIELD_PATH_MIN)}</p>}
                                </div>
                                )}
                                {showMax && (
                                <div className="grid gap-2">
                                    <Label htmlFor={`${prefix}-max`}>{i18n.attributeDefinitions.validationRules.max}</Label>
                                    <Input
                                        id={`${prefix}-max`}
                                        data-testid={`${prefix}-max`}
                                        type="number"
                                        value={max ?? ''}
                                        onChange={e => setValidationRule(RULE_MAX, e.target.value)}
                                        placeholder={i18n.attributeDefinitions.validationRules.maxPlaceholder}
                                        disabled={disabled}
                                        className={errorStyle(FIELD_PATH_MAX)}
                                    />
                                    {fieldError(FIELD_PATH_MAX) && <p data-testid={`${prefix}-validationRules/max-error`} className="text-sm text-destructive">{fieldError(FIELD_PATH_MAX)}</p>}
                                </div>
                                )}
                            </div>
                        )}

                        <div className="grid gap-2">
                            <Label htmlFor={`${prefix}-maxocc`}>{i18n.common.maxValues}</Label>
                            <Input
                                id={`${prefix}-maxocc`}
                                data-testid={`${prefix}-maxocc`}
                                type="number" min={1}
                                value={maxOccurrences ?? ''}
                                onChange={e => setMaxOccurrences(e.target.value)}
                                disabled={disabled}
                            />
                            {maxOccurrences != null && maxOccurrences < 1 && <p data-testid={`${prefix}-maxocc-inline-error`} className="text-sm text-destructive">{i18n.attributeDefinitions.validationRules.mustBeAtLeastOne}</p>}
                        </div>
                    </div>
                </AccordionContent>
            </AccordionItem>
        </Accordion>

            <SqlEditorDialog
                open={sqlEditorOpen}
                onOpenChange={setSqlEditorOpen}
                sql={values.validationRules?.valueSource?.sql ?? ''}
                onApply={handleSqlApply}
                parametersHint={sourceType === ValueSourceType.SQL_LABELED ? i18n.attributeDefinitions.sqlHintTwoColumns : i18n.attributeDefinitions.sqlHintOneColumn}
                validateResult={result => {
                    const expected = sourceType === ValueSourceType.SQL_LABELED ? 2 : 1
                    if (result.columns.length !== expected) {
                        return expected === 2
                            ? i18n.attributeDefinitions.sqlLabeledColumnCount({count: result.columns.length, columns: result.columns.map(c => c.name).join(', ')})
                            : i18n.attributeDefinitions.sqlTooManyColumns({count: result.columns.length, columns: result.columns.map(c => c.name).join(', ')})
                    }
                    return null
                }}
                maxRows={5}
            />
        </>
    )
}
